<template>
  <div id="formCard">
    <b-form class="row" @submit.prevent="onSubmit">
      <b-card class="form-card">
        <h5 class="mb-2 form-title">{{ formTitle }}</h5>
        <h6 class="form-subtitle">{{$t('common.notes.fillInInformationBelow')}}</h6>
        <!-- name input  -->
        <div class="name mb-3">
          <label for="name" class="mb-0 no-wrap no-wrap input-label">{{ $t('common.name') }}:</label>
          <b-input
            id="name"
            name="name"
            type="text"
            v-model.trim="form.name"
            :placeholder="$t('common.name')"
            @input="checkUserTypeDuplication"
          />
          
          <div class="err-wrapper">
            <div v-if="userTypeDuplication">
              <span class="error-value">{{ $t('userTypes.form.userTypeNameAlreadyExists') }}</span>
            </div>
            <div v-if="submitted && !$v.form.name.required">
              <span class="error-value">{{ $t('userTypes.form.userTypeNameRequired') }}</span>
            </div>
            <div v-else-if="submitted && !$v.form.name.maxLength">
              <span class="error-value">{{ $t('common.validation.maximumLength20Characters') }}</span>
            </div>
          </div>
        </div>
        <!-- registration-method  -->
        <div class="registration-method mb-3">
          <label for="method" class="mb-0 no-wrap no-wrap input-label"
            >{{ $t('userTypes.registrationMethod') }}:</label
          >
          <multiselect
            id="method"
            v-model="form.registration_method"
            :options="isFoundParent ? methodstWithOutRegistrationNotRequiredMethod : registration_method_list"
            @select="onShowGracePeriodInput ? form.visit_grace_period = 30 : ''"
            :placeholder="$t('userTypes.form.selectRegistrationMethod')"
            label="name"
            :show-labels="false"
            :allowEmpty="true"
          />
          <div class="err-wrapper">
            <div v-if="submitted && !$v.form.registration_method.required">
              <span class="error-value">{{ $t('userTypes.form.registrationMethodRequired') }}</span>
            </div>
          </div>
          <p v-if="onShowUndefinedUsersText" class="mb-0 text-muted">
            {{ $t('userTypes.form.undefinedUsersSignInCompanyAsThisUserTypeIfAdded') }}
          </p>
        </div>
        <!-- visit grace period  -->
        <div class="period mb-3" v-if="onShowGracePeriodInput">
          <label for="period" class="mb-0 no-wrap no-wrap input-label"
            >{{ $t('userTypes.form.visitGracePeriodInMinutes') }}</label
          >
          <b-input
            id="period"
            name="period"
            type="number"
            min="30"
            v-model="form.visit_grace_period"
            :placeholder="$t('userTypes.form.enterGracePeriodInMinutes')"
          />
          <div class="err-wrapper">
            <div v-if="submitted && !$v.form.visit_grace_period.required">
              <span class="error-value">{{ $t('userTypes.form.visitGracePeriodRequired') }}</span>
            </div>
            <div
              v-if="submitted && form.visit_grace_period && form.visit_grace_period < 30"
            >
              <span class="error-value"
                >{{ $t('userTypes.form.visitGracePeriodMustLeast30Minutes') }}</span
              >
            </div>
            <div v-else-if="submitted && !$v.form.visit_grace_period.maxLength">
              <span class="error-value">{{ $t('common.validation.maximumLength4Digits') }}</span>
            </div>
          </div>
        </div>
        <!-- switch user type  -->
        <div class="switch-user-type mb-3" v-if="onShowSwitchUserType">
          <b-form-checkbox
            v-model="form.switch_user_type"
            class="custom-checkbox-primary"
            checked
          >
          {{ $t('userTypes.form.giveUserAbilitySwitchUserType') }}
          </b-form-checkbox>
          <p class="mb-0 text-muted">
            {{ $t('userTypes.form.forExampleStaffMemberMessage') }}
          </p>
          <hr/>
        </div>

        <div class="vp-mt-24" v-if="showDataMandatorySection">
          <p class="vp-mb-12">{{ $t('userTypes.form.dataMandatoryToBeFilledByUser') }}</p>
          <b-form-checkbox
            class="custom-checkbox-primary"
            v-model="form.profile_photo"
            checked
          >
            {{ $t('userTypes.form.profilePhoto') }}
          </b-form-checkbox>
        </div>

        <div class="action-btns">
          <button
            type="submit"
            :disabled="getLoading || disableWatcher || userTypeDuplication"
            class="submit-btn btn btn-primary"
            size="md"
          >
            <i
              v-if="getLoading"
              class="bx bx-loader bx-spin font-size-16 align-middle mr-2 rounded"
            ></i>
            <span class="cursor-pointer" v-else> {{ $t('common.submit') }} </span>
          </button>
          <router-link to="/user-types">
            <button class="btn btn-outline-danger cancel-btn">{{ $t('common.cancel') }}</button>
          </router-link>
        </div>
      </b-card>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { required, maxLength, requiredIf, numeric } from "vuelidate/lib/validators";

export default {
  props: ["parentTypes", "mode", "parentId", "formTitle", "parentSettings", "parentName", "isFoundParent"],
  data: function () {
    return {
      userTypes: [],
      form: {
        parentId: null,
        type: null,
        name: "",
        parent: null,
        subParent: null,
        entity_id: null,
        registration_method: null,
        visit_grace_period: 30,
        switch_user_type: false,
        profile_photo: false,
      },
      isFormSubmitted: false,
      registration_method_list: [
        {
          id: "PRE_REGISTERED_ONLY",
          name: this.$t("preRegisteration.preRegisteredOnly"),
        },
        {
          id: "PRE_REGISTRATION_WITH_APPROVAL_REQUIRED",
          name: this.$t("preRegisteration.preRegistrationWithApprovalRequired"),
        },
        {
          id: "REGISTRATION_ALLOWED",
          name: this.$t("preRegisteration.registrationAllowed"),
        },
        {
          id: "PRE_REGISTERED_BY_ADMIN",
          name: this.$t("preRegisteration.preRegisteredByAdmin"),
        },
        {
          id: "REGISTRATION_NOT_REQUIRED",
          name: this.$t("preRegisteration.registrationNotRequired"),
        }
      ],
      methodstWithOutRegistrationNotRequiredMethod: [
        {
          id: "PRE_REGISTERED_ONLY",
          name: "Pre-registered only",
        },
        {
          id: "PRE_REGISTRATION_WITH_APPROVAL_REQUIRED",
          name: "Pre-registration with approval required",
        },
        {
          id: "REGISTRATION_ALLOWED",
          name: "Registration allowed",
        },
        {
          id: "PRE_REGISTERED_BY_ADMIN",
          name: "Pre-registered by admin",
        }
      ],
      showMandatoryDataForUserTypesList:[
        "PRE_REGISTERED_BY_ADMIN",
        "REGISTRATION_ALLOWED",
        "PRE_REGISTRATION_WITH_APPROVAL_REQUIRED",
        "PRE_REGISTERED_ONLY"
      ],
      parent_user_type: null,
      submitted: false,
      userTypeDuplication : false,
    };
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(20) },
      registration_method: { required },
      visit_grace_period: {
        required: requiredIf(function () {
          return this.onShowGracePeriodInput;
        }),
        numeric,
        maxLength: maxLength(4),
      },
    },
  },
  watch: {
    parentName(name) {
      this.parent_user_type = name;
    },
    onShowGracePeriodInput(value) {
      !value && (this.form.visit_grace_period = null);
    },
  },
  computed: {
    ...mapGetters("userType", ["getLoading"]),
    ...mapGetters("authfack", ["currentEntity"]),
    showDataMandatorySection(){
      return this.form.registration_method?.id ? this.showMandatoryDataForUserTypesList.includes(this.form.registration_method.id) : false;
    },
    disableWatcher() {
      const { name } = this.form;
      return name ? false : true;
    },
    onShowGracePeriodInput() {
      if (this.form.registration_method) {
        return (
          this.form.registration_method.name == this.$t("preRegisteration.preRegisteredOnly") ||
          this.form.registration_method.name == "Pre-registration with approval required"
        );
      }
      return false;
    },
    onShowSwitchUserType() {
      if (this.form.registration_method) {
        return this.form.registration_method.name == this.$t("preRegisteration.preRegisteredByAdmin");
      }
      return false;
    },
    onShowUndefinedUsersText() {
      if (this.form.registration_method) {
        return this.form.registration_method.name == this.$t("preRegisteration.registrationNotRequired");
      }
      return false;
    },
  },
  methods: {
    ...mapActions("userType", ['createUserType', 'fetchUserTypeDuplication']),
    checkUserTypeDuplication(value){
      this.userTypeDuplication = false;
      this.fetchUserTypeDuplication({entity_id : this.currentEntity.id,  value : value}).then((data)=>{
        // console.log('data', data);
        this.userTypeDuplication = data.data;
      });
    },
    onSubmit() {
      this.submitted = true;
      let entity_id = this.currentEntity.id;
      let { parentId } = this;
      this.$v.$touch();
      let { name, switch_user_type, visit_grace_period, registration_method, profile_photo } = this.form;
      if (visit_grace_period < 30) return;
      if (!this.$v.$error) {
        this.createUserType({
          userType: {
            name,
            parent_id: parentId,
            entity_id,
          },
          settings: {
            registration_method: registration_method && registration_method.id,
            visit_grace_period: visit_grace_period ? visit_grace_period : 30,
            switch_usertype: switch_user_type ? 1 : 0,
            profile_photo : this.showDataMandatorySection ? Number(profile_photo) : 0
          },
        });
      }
    },
  },
  beforeMount() {
    this.form.parentId = this.parentId;
    this.form.entity_id = this.currentEntity.id;
  },
};
</script>

<style lang="scss" scoped>
$textColor: #495057;
#formCard {
  border-radius: 25px;
  position: relative;
  .form-card {
    width: 100%;
    .form-subtitle {
      color: #74788d;
      letter-spacing: 0.25px;
      line-height: 16px;
      font-size: 14px;
      margin-top: 0px;
      margin-inline-end: 10px;
      margin-bottom: 20px;
      margin-inline-start: 0px;
    }
    .action-btns {
      margin-top: 40px;
    }
    .err-wrapper {
      div {
        margin-top: 5px;
      }
    }
  }
}
</style>
<style lang="scss">
div#formCard{
  .multiselect__content-wrapper{
    max-height: 150px!important;
  }
}
</style>
